<template>
  <EditorPanel :loading-save="loadingSave" @confirm="saveSettings">
    <div class="d-flex justify-space-between align-center">
      <EditorSectionLabel :text="$t('harmony.add-photoUser')" class="ma-0" />
      <EditorIconButton
        v-if="harmony.logo_path"
        icon="mdi-trash-can-outline"
        :aria-label="$t('harmony.remove-logo')"
        @click="removeLogo"
      />
    </div>
    <ImageUpload
      :key="logoKey"
      uploadEndpoint="/upload-site-logo"
      :siteId="harmony.id"
      selected="logo"
      :previewUrl="harmony.logo_path"
      @image-uploaded="handleImageUploaded"
      class="mt-2"
    />

    <div class="d-flex flex-column mt-2">
      <EditorSectionLabel text="Forma de perfil" />
      <v-row no-gutters class="mt-1">
        <v-col cols="12" class="px-1 py-1">
          <v-btn
            :outlined="siteSetting.profile.shape !== 'rectangle'"
            :elevation="0"
            color="#9BA9BD"
            class="rounded-lg w-100"
            small
            block
            :aria-label="$t('harmony.shape-rectangle')"
            :aria-pressed="siteSetting.profile.shape === 'rectangle'"
            @click="siteSetting.profile.shape = 'rectangle'"
          >
            Rectangular
          </v-btn>
        </v-col>
        <v-col cols="6" class="px-1 py-1">
          <v-btn
            :outlined="siteSetting.profile.shape !== 'circle'"
            :elevation="0"
            color="#9BA9BD"
            class="rounded-lg w-100"
            small
            block
            :aria-label="$t('harmony.shape-circle')"
            :aria-pressed="siteSetting.profile.shape === 'circle'"
            @click="siteSetting.profile.shape = 'circle'"
          >
            Círcular
          </v-btn>
        </v-col>
        <v-col cols="6" class="px-1 py-1">
          <v-btn
            :outlined="siteSetting.profile.shape !== 'square'"
            :elevation="0"
            color="#9BA9BD"
            class="rounded-lg w-100"
            small
            block
            :aria-label="$t('harmony.shape-square')"
            :aria-pressed="siteSetting.profile.shape === 'square'"
            @click="siteSetting.profile.shape = 'square'"
          >
            Cuadrado
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <EditorSectionLabel :text="$t('harmony.add-backgroundColor')" class="mt-3 mb-1" />
    <div class="d-flex align-center mt-1" style="gap: 8px;">
      <v-btn
        :outlined="selectBackground !== 1 || !!siteSetting.page.background"
        :elevation="0"
        color="#9BA9BD"
        class="rounded-lg flex-grow-1"
        small
        @click="selectColorMode"
      >
        {{ $t("harmony.add-color") }}
      </v-btn>

      <v-btn
        :outlined="selectBackground !== 2 && !siteSetting.page.background"
        :elevation="0"
        color="#9BA9BD"
        class="rounded-lg flex-grow-1"
        small
        @click="selectImageMode"
      >
        {{ $t("harmony.add-image") }}
      </v-btn>

      <EditorIconButton
        v-if="hasCustomBackground"
        icon="mdi-trash-can-outline"
        :aria-label="$t('harmony.remove-background')"
        @click="removeBackground"
      />
    </div>

    <!-- Color Mode -->
    <div v-if="selectBackground == 1 && !siteSetting.page.background" class="mt-2">
      <div class="d-flex align-center justify-start mt-2 mb-2" style="gap: 8px;">
        <v-tooltip bottom color="#8e84c0">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              :style="{ backgroundColor: selectColorGradient.a }"
              :disabled="loadingSave"
              class="panelColors"
              :class="{ 'panelColors--active': selectNumberGradient === 1 }"
              :aria-label="$t('harmony.color-first')"
              @click="selectNumberGradient = 1"
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <span>{{ $t('harmony.color-first') }}</span>
        </v-tooltip>

        <v-tooltip bottom color="#8e84c0">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              :style="{ backgroundColor: selectColorGradient.b }"
              :disabled="loadingSave"
              class="panelColors"
              :class="{ 'panelColors--active': selectNumberGradient === 2 }"
              :aria-label="$t('harmony.color-second')"
              @click="selectNumberGradient = 2"
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <span>{{ $t('harmony.color-second') }}</span>
        </v-tooltip>

        <v-tooltip bottom color="#8e84c0">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              :style="{ backgroundColor: '#e1dcfa' }"
              :disabled="loadingSave"
              class="panelColors"
              :aria-label="$t('harmony.color-direction')"
              @click="rotateImage"
              v-bind="attrs"
              v-on="on"
            >
              <img
                src="@/assets/illustrations/arrow.png"
                class="arrow-icon"
                alt=""
                :style="{ transform: 'rotate(' + rotationAngle + 'deg)' }"
                aria-hidden="true"
              />
            </v-btn>
          </template>
          <span>{{ $t('harmony.color-direction') }}</span>
        </v-tooltip>
      </div>

      <v-color-picker
        v-if="selectNumberGradient == 1"
        v-model="selectColorGradient.a"
        dot-size="25"
        hide-inputs
        hide-mode-switch
        swatches-max-height="200"
        canvas-height="100"
        @input="updateGradient"
        class="remove-preview mx-auto mt-2"
      />

      <v-color-picker
        v-if="selectNumberGradient == 2"
        v-model="selectColorGradient.b"
        dot-size="25"
        hide-inputs
        hide-mode-switch
        swatches-max-height="200"
        canvas-height="100"
        @input="updateGradient"
        class="remove-preview mx-auto mt-2"
      />
    </div>

    <!-- Image Mode -->
    <ImageUpload
      v-if="selectBackground == 2 || siteSetting.page.background"
      uploadEndpoint="/upload-background"
      selected="background"
      cardSelected="page-background"
      :siteId="harmony.id"
      :previewUrl="siteSetting.page.background"
      @image-uploaded="handleImageUploaded"
      class="mt-3"
    />

    <!-- Patterns Section -->
    <div class="d-flex justify-space-between align-center mt-3">
      <EditorSectionLabel class="ma-0">
        {{ $t("harmony.add-backgroundPatterns") }}
        <span style="color: #8e84c0">{{ siteSetting.page.pattern }}</span>
      </EditorSectionLabel>
      <EditorIconButton
        v-if="siteSetting.page.pattern"
        icon="mdi-trash-can-outline"
        :aria-label="$t('harmony.remove-pattern')"
        @click="removePattern"
      />
    </div>
    <div class="frame-div patterns-div mt-2">
      <v-row no-gutters>
        <div
          v-for="pattern in patterns"
          :key="pattern.id"
          class="patternDiv mt-1"
          :class="{ selected: siteSetting.page.pattern === pattern.id }"
          @click="setPattern(pattern.id)"
          :aria-label="pattern.alt"
          :aria-pressed="siteSetting.page.pattern === pattern.id"
          tabindex="0"
          @keyup.enter="setPattern(pattern.id)"
          @keyup.space.prevent="setPattern(pattern.id)"
        >
          <img :src="pattern.src" :alt="pattern.alt" aria-hidden="true" class="icon">
        </div>
      </v-row>
    </div>
  </EditorPanel>
</template>

<script>
import EditorPanel from './EditorPanel.vue'
import EditorSectionLabel from './shared/EditorSectionLabel.vue'
import EditorIconButton from './shared/EditorIconButton.vue'

export default {
  name: 'EditorLogoPanel',
  components: {
    EditorPanel,
    EditorSectionLabel,
    EditorIconButton,
    ImageUpload: () => import('@/components/harmony/uploads/ImageUpload.vue')
  },
  props: {
    harmony: {
      type: Object,
      required: true
    },
    siteSetting: {
      type: Object,
      required: true
    },
    loadingSave: {
      type: Boolean,
      default: false
    },
    patterns: {
      type: Array,
      default: () => []
    },
    initialSelectColorGradient: {
      type: Object,
      default: () => ({ a: '', b: '', direction: '' })
    },
    initialRotationAngle: {
      type: Number,
      default: 90
    },
    selectBackground: {
      type: Number,
      default: 1
    }
  },
  data() {
    return {
      logoKey: 0,
      selectNumberGradient: 1,
      selectColorGradient: { ...this.initialSelectColorGradient },
      rotationAngle: this.initialRotationAngle,
      directions: [
        'to left', 'to left top', 'to top', 'to right top',
        'to right', 'to right bottom', 'to bottom', 'to left bottom'
      ]
    }
  },
  computed: {
    hasCustomBackground() {
      const bg = this.siteSetting?.page?.background;
      const c1 = this.siteSetting?.page?.color?.color1;
      const c2 = this.siteSetting?.page?.color?.color2;
      return !!bg || (c1 && c1 !== '#D4D4D4') || (c2 && c2 !== '#D4D4D4');
    }
  },
  watch: {
    'harmony.logo_path'() {
      this.logoKey++
    },
    initialSelectColorGradient: {
      handler(newVal) {
        this.selectColorGradient = { ...newVal }
      },
      deep: true
    },
    initialRotationAngle(newVal) {
      this.rotationAngle = newVal
    }
  },
  methods: {
    saveSettings() {
      this.$emit('save-settings')
    },
    removeLogo() {
      this.$emit('remove-logo')
    },
    removeBackground() {
      if (this.siteSetting.page.background) {
        this.$emit('remove-background', this.siteSetting.page.background)
      } else {
        this.$emit('remove-background-color')
      }
    },
    selectColorMode() {
      this.$emit('update:selectBackground', 1)
    },
    selectImageMode() {
      this.$emit('update:selectBackground', 2)
    },
    removePattern() {
      this.$emit('set-pattern', '')
    },
    handleImageUploaded() {
      this.$emit('image-uploaded')
    },
    rotateImage() {
      if (this.rotationAngle === 315) {
        this.rotationAngle = 0
      } else {
        this.rotationAngle += 45
      }
      const index = this.rotationAngle / 45
      this.selectColorGradient.direction = this.directions[index]
      this.updateGradient()
      this.$emit('rotate-image', this.rotationAngle)
    },
    updateGradient() {
      this.siteSetting.page.color = {
        color1: this.selectColorGradient.a,
        color2: this.selectColorGradient.b,
        direction: this.selectColorGradient.direction
      }
      this.$emit('update-gradient', this.selectColorGradient)
    },
    setPattern(patternId) {
      this.$emit('set-pattern', patternId)
    }
  }
}
</script>

<style scoped>
@import './editor-panel.css';

.panelColors--active {
  box-shadow: 0 0 0 2px var(--editor-color-primary, #8e84c0) !important;
  transform: scale(1.15);
}
</style>
